import { useEffect, useState } from 'react';
import {
  isAudioUnlocked,
  onAudioUnlockChange,
  playAudioFromPath,
  unlockAudio,
} from '../lib/audio';
import { colors } from '../theme';

type Props = {
  // Path of the most recent playable incoming message, if any. Tapping the
  // banner will play this directly — a real audio file is a rock-solid iOS
  // unlock, unlike silent data URIs which sometimes get rejected.
  latestPlayablePath?: string | null;
};

// Fallback UX for iOS receivers who haven't tapped the page since loading.
// iOS locks HTMLAudioElement playback behind a user gesture; if the user
// signed in and has been passively receiving, they never unlock and hear
// nothing. One tap on this banner guarantees an unlock.
export function AudioUnlockBanner({ latestPlayablePath }: Props) {
  const [unlocked, setUnlocked] = useState(() => isAudioUnlocked());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const off = onAudioUnlockChange(setUnlocked);
    return off;
  }, []);

  if (unlocked) return null;

  const onTap = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      if (latestPlayablePath) {
        // Plays the real clip AND unlocks the element in one step.
        await playAudioFromPath(latestPlayablePath);
      } else {
        await unlockAudio();
      }
    } catch (e: any) {
      setErr(e?.message ?? 'Playback failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button className="audio-unlock" onClick={onTap} disabled={busy}>
      {err ? `🔇 ${err}` : '🔊 Tap to enable sound'}
      <style>{`
        .audio-unlock {
          position: absolute;
          top: calc(env(safe-area-inset-top) + 72px);
          left: 50%;
          transform: translateX(-50%);
          background: ${colors.accent};
          color: ${colors.bg};
          border: none;
          border-radius: 999px;
          padding: 10px 18px;
          font-size: 13px;
          font-weight: 700;
          z-index: 1200;
          box-shadow: 0 4px 14px rgba(0,0,0,0.5);
          cursor: pointer;
        }
        .audio-unlock:disabled { opacity: 0.6; }
      `}</style>
    </button>
  );
}
